import { SrInsightType as InsightType } from '@plato/data/starrocks/types';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { roundAbsToHundred } from '@plato/ui-legacy/lib/round-abs-to-hundred';
import {
    Card,
    CardActions,
    CardActionsGroup,
    CardContent,
    CardDetail,
    CardExpandAction,
    CardExpandContent,
    CardFront,
    CardHeader,
    CardStart,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import { Button } from '@plato/ui/primitives/button';
import { Skeleton } from '@plato/ui/primitives/skeleton';
import { cn } from '@plato/ui/util/classes';
import { PlusIcon, TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrencyOrEmpty } from '../../../components/typography/currency-or-empty.tsx';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { InsightReasonText } from '../../insights-common/components/insight-reason-text.tsx';
import { MoneyDeltaBadge } from '../../insights-common/components/money-delta-badge.tsx';
import type { SendAnalyticsEventHandler } from '../../insights-common/hooks/use-insights-with-analytics.ts';
import { getInsightName } from '../../insights-common/lib/insight-name.ts';

export type LogInteractionInsightCardProps = {
    insightData: InsightListItem;
    onCreateTask: () => void;
    onAnalyticsEvent: SendAnalyticsEventHandler;
};

// The expanded card lists only the first few articles behind the number (the
// detail dialog shows the full breakdown); the rest collapse into a "N more".
const maxVisibleArticles = 3;

export function LogInteractionInsightCard({
    insightData,
    onCreateTask,
    onAnalyticsEvent,
}: LogInteractionInsightCardProps): ReactNode {
    const { t } = useTranslation('notes');

    const [isOpen, setIsOpen] = useState(false);

    const { insight, details } = insightData;
    const name = getInsightName(t, insight);
    const category = details.category.title ?? insight.actionable_entity_id;
    const DirectionIcon =
        insight.insight_type === InsightType.CHURN_WARNING ? TrendingDownIcon : TrendingUpIcon;

    return (
        <Card
            onOpenChange={(open) => {
                setIsOpen(open);
                if (open) {
                    onAnalyticsEvent({
                        event: 'sales_insight_expanded',
                        payload: { sales_insight_id: insight.id },
                    });
                }
            }}
        >
            <CardFront>
                <CardStart>
                    <DirectionIcon />
                </CardStart>
                <CardContent>
                    <CardHeader className="flex-nowrap items-center">
                        <CardTitle className="min-w-0 flex-1 truncate font-semibold">{name}</CardTitle>
                        <CardSubtitle className="shrink-0 items-center tabular-nums">
                            <MoneyDeltaBadge>
                                {roundAbsToHundred(insight.absolute_number ?? 0)}
                            </MoneyDeltaBadge>
                        </CardSubtitle>
                    </CardHeader>
                    <CardDetail className="truncate text-center card-expanded:text-content @md:text-left">
                        {category}
                    </CardDetail>
                </CardContent>
                <CardExpandAction className="self-start" />
            </CardFront>
            <CardExpandContent>
                <div className="flex min-w-0 flex-1 flex-col gap-container">
                    <span className="text-body text-content-weak">
                        <InsightReasonText insight={insight} categoryName={category} />
                    </span>
                    {isOpen && <InsightArticles insightData={insightData} />}
                </div>
            </CardExpandContent>
            <CardActions>
                <CardActionsGroup className="grow justify-end">
                    <Button variant="primary" onClick={onCreateTask}>
                        <PlusIcon /> {t('notes__ai_insights_task_action')}
                    </Button>
                </CardActionsGroup>
            </CardActions>
        </Card>
    );
}

// The article breakdown behind the insight's number — churn warnings carry
// per-product revenue + delta, product recommendations are name-only. Same
// detail endpoints the insight dialog reads; capped to the top rows here.
function InsightArticles({ insightData }: { insightData: InsightListItem }): ReactNode {
    if (insightData.insight.insight_type === InsightType.CHURN_WARNING) {
        return <ChurnArticles insightData={insightData} />;
    }
    return <RecommendationArticles insightData={insightData} />;
}

function ChurnArticles({ insightData }: { insightData: InsightListItem }): ReactNode {
    const { data, isLoading } =
        api.platoUser.query.insights.getInsightExtendedView.getChurnWarningDetail.useQuery({
            insightId: insightData.insight.id,
        });

    if (isLoading) {
        return <ArticlesLoading />;
    }

    const products = data ?? [];
    const shown = products.slice(0, maxVisibleArticles);
    const rest = products.length - shown.length;

    if (shown.length === 0) {
        return null;
    }

    return (
        <ArticleList rest={rest}>
            {shown.map((product) => {
                const delta = product.revenue_delta_percentage;
                const up = delta === null || delta === 0 ? null : delta > 0;
                return (
                    <ArticleRow key={product.id} name={product.name}>
                        <span className="shrink-0 text-body tabular-nums text-content-weak">
                            <CurrencyOrEmpty showEmptyIfZero>{product.revenue_current}</CurrencyOrEmpty>
                        </span>
                        <span
                            className={cn(
                                'w-16 shrink-0 text-right text-body tabular-nums',
                                up === null ? 'text-content-weak' : up ? 'text-success' : 'text-danger',
                            )}
                        >
                            <PercentageOrEmpty showPlusSignIfPositive showEmptyIfZero>
                                {delta}
                            </PercentageOrEmpty>
                        </span>
                    </ArticleRow>
                );
            })}
        </ArticleList>
    );
}

function RecommendationArticles({ insightData }: { insightData: InsightListItem }): ReactNode {
    const { data, isLoading } =
        api.platoUser.query.insights.getInsightExtendedView.getProductRecommendationsDetail.useQuery({
            insightId: insightData.insight.id,
        });

    if (isLoading) {
        return <ArticlesLoading />;
    }

    const products = data ?? [];
    const shown = products.slice(0, maxVisibleArticles);
    const rest = products.length - shown.length;

    if (shown.length === 0) {
        return null;
    }

    return (
        <ArticleList rest={rest}>
            {shown.map((product) => (
                <ArticleRow key={product.product_variant_id} name={product.product_variant_name} />
            ))}
        </ArticleList>
    );
}

function ArticleList({ rest, children }: { rest: number; children: ReactNode }): ReactNode {
    const { t } = useTranslation('notes');
    return (
        <div className="flex flex-col divide-y divide-line-weak">
            {children}
            {rest > 0 && (
                <span className="py-cozy text-body text-content-weak">
                    {t('notes__ai_insights_more_articles', { count: rest })}
                </span>
            )}
        </div>
    );
}

function ArticleRow({ name, children }: { name: string; children?: ReactNode }): ReactNode {
    return (
        <div className="flex items-center gap-cozy py-cozy">
            <span className="min-w-0 flex-1 truncate text-body text-content">{name}</span>
            {children}
        </div>
    );
}

function ArticlesLoading(): ReactNode {
    return (
        <div className="flex flex-col gap-container-cozy py-container-tight">
            <Skeleton className="w-full" />
            <Skeleton className="w-4/5" />
            <Skeleton className="w-3/5" />
        </div>
    );
}
